import React from "react";
import { LegalLayout, Section } from "@/components/LegalLayout";

export default function Refund() {
  return (
    <div data-testid="refund-page">
      <LegalLayout title="Refund Policy" updated="2026-05-17">
        <p className="text-zinc-300 leading-relaxed text-[15px]">
          We want you to be satisfied with every CloudRent purchase. This Refund Policy
          explains when you are entitled to a refund and how to request one. It applies
          to all purchases made through the CloudRent platform. Refunds are issued to
          the original payment method.
        </p>

        <Section n={1} title="14-Day Money-Back Guarantee">
          <p>
            You may request a full refund within <strong>14 calendar days</strong> of your
            first purchase of any server plan, provided the server has not been used
            beyond normal evaluation:
          </p>
          <ul className="list-disc list-inside space-y-1 text-zinc-300">
            <li>The server has been online for less than 72 hours of cumulative runtime, and</li>
            <li>Less than 100 GB of outbound bandwidth has been consumed.</li>
          </ul>
          <p>This guarantee covers your first purchase only; renewals are non-refundable except where required by law.</p>
        </Section>

        <Section n={2} title="Recurring Renewals">
          <p>
            Recurring plan renewals are non-refundable once charged. To prevent the next
            charge, cancel from your dashboard at least 24 hours before the renewal date.
            Cancellation takes effect at the end of the current billing period; you keep
            access until then.
          </p>
        </Section>

        <Section n={3} title="Service Failure Refunds">
          <p>
            If we materially fail to deliver the server you paid for (extended outage,
            broken provisioning, or undelivered credentials) we will:
          </p>
          <ul className="list-disc list-inside space-y-1 text-zinc-300">
            <li>Refund the affected billing period in full, or</li>
            <li>Credit your account for the equivalent of the affected period, at your choice.</li>
          </ul>
          <p>SLA-eligible downtime credits are calculated per service-specific SLA documentation.</p>
        </Section>

        <Section n={4} title="Non-Refundable Items">
          <p>The following are not refundable:</p>
          <ul className="list-disc list-inside space-y-1 text-zinc-300">
            <li>One-time setup fees once provisioning has completed.</li>
            <li>Domain registrations or third-party software licenses purchased through us.</li>
            <li>Plans terminated by us for violation of the Acceptable Use section of the Terms of Service.</li>
            <li>Dedicated bare-metal servers after 72 hours of cumulative runtime.</li>
          </ul>
        </Section>

        <Section n={5} title="How to Request a Refund">
          <p>Email <a className="text-signal hover:underline" href="mailto:billing@cloudrentvps.com">billing@cloudrentvps.com</a> with:</p>
          <ul className="list-disc list-inside space-y-1 text-zinc-300">
            <li>Your account email.</li>
            <li>The order ID (visible in your dashboard).</li>
            <li>A brief reason.</li>
          </ul>
          <p>
            We respond within 2 business days. Approved refunds are returned to the
            original payment method within 5–10 business days, depending on your card
            issuer.
          </p>
        </Section>

        <Section n={6} title="Chargebacks">
          <p>
            Please contact us first — most issues are resolved within 48 hours. Filing a
            chargeback without contacting us may result in your account being suspended
            pending investigation.
          </p>
        </Section>

        <Section n={7} title="Consumer Law">
          <p>
            EU/UK consumers retain their statutory 14-day right of withdrawal under the
            Consumer Rights Directive. By starting to use a server before the withdrawal
            period ends, you acknowledge that you may lose this right for the consumed
            portion of the service.
          </p>
        </Section>
      </LegalLayout>
    </div>
  );
}
